/**
 * Export communities data to Excel
 */

import * as XLSX from 'xlsx-js-style';
import { ProfileStatus, type CommunityProfile } from '../types/communities-types';
import { format } from 'date-fns';

interface Actor {
  actorId: number;
  name?: string;
}

interface ExcelRow {
  'Community Name'?: string;
  'Location'?: string;
  'Description'?: string;
  'Discount Type'?: string;
  'Discount Value'?: string;
  'Start Date'?: string;
  'Expiration Date'?: string;
  'Status'?: string;
  'Total Accounts'?: string;
}

/**
 * Calculate optimal column width based on content
 */
const calculateColumnWidth = (data: ExcelRow[], key: keyof ExcelRow, headerLength: number): number => {
  const maxLength = data.reduce((max, row) => {
    const cellLength = row[key]?.toString().length || 0;
    return Math.max(max, cellLength);
  }, headerLength);

  // Add some padding and cap at reasonable maximum
  return Math.min(Math.max(maxLength + 2, 10), 60);
};

/**
 * Format date to DD/MM/YYYY for Excel
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    // Parse the date string (YYYY-MM-DD or ISO format)
    const date = new Date(dateString);
    return format(date, 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
};

/**
 * Get discount type label
 */
const getDiscountTypeLabel = (strategyType: number | string): string => {
  const type = typeof strategyType === 'number' ? strategyType : parseInt(strategyType);
  switch (type) {
    case 0:
      return 'Energy Percentage'; // FixedPercentageEnergy
    case 1:
      return 'Energy Amount (Per kWh)'; // FixedCentsPerKwh
    case 2:
      return 'Total Percentage'; // FixedPercentage
    default:
      return 'Unknown';
  }
};

/**
 * Get discount value with unit
 */
const getDiscountValue = (strategyType: number | string, value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';

  const type = typeof strategyType === 'number' ? strategyType : parseInt(strategyType);
  switch (type) {
    case 0:
      return `${value}%`; // FixedPercentageEnergy - percentage discount
    case 1:
      return value.toString(); // FixedCentsPerKwh - cents per kWh (no unit)
    case 2:
      return `${value}%`; // FixedPercentage - percentage discount
    default:
      return value.toString();
  }
};

/**
 * Get status label
 */
const getStatusLabel = (profileStatus: string | number): string => {
  // Handle both string and number types from API
  if (typeof profileStatus === 'string') {
    return profileStatus === 'Active' ? 'Active' : 'Inactive';
  }
  return profileStatus === ProfileStatus.Active ? 'Active' : 'Inactive';
};

/**
 * Export communities to Excel file
 */
export const exportCommunitiesToExcel = (
  communities: CommunityProfile[],
  actors: Actor[] = [],
  filename?: string
) => {
  if (!communities || communities.length === 0) {
    throw new Error('No data to export');
  }

  // Prepare data for Excel export
  const excelData: ExcelRow[] = communities.map(community => {
    // Find location name by actorId
    const location = actors.find(actor => actor.actorId === community.actorId);
    const locationName = location?.name || `Actor ${community.actorId}`;

    return {
      'Community Name': community.profileName,
      'Description': community.profileDescription || 'N/A',
      'Location': locationName,
      'Discount Type': getDiscountTypeLabel(community.strategyType),
      'Discount Value': getDiscountValue(community.strategyType, community.singleDimensionValue),
      'Start Date': formatDate(community.startDate),
      'Status': getStatusLabel(community.profileStatus),
      'Total Accounts': (community.assignedAccounts ?? 0).toString(),
    };
  });

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Auto-resize columns based on content
  const headers: (keyof ExcelRow)[] = [
    'Community Name',
    'Description',
    'Location',
    'Discount Type',
    'Discount Value',
    'Start Date',
    'Status',
    'Total Accounts',
  ];

  const columnWidths = headers.map(header => ({
    wch: calculateColumnWidth(excelData, header, header.length)
  }));

  worksheet['!cols'] = columnWidths;

  // Style the header row
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!worksheet[cellAddress]) continue;

    worksheet[cellAddress].s = {
      font: { bold: true, sz: 12 },
      fill: { fgColor: { rgb: 'F3F4F6' } },
      border: {
        bottom: { style: 'thin', color: { rgb: '000000' } }
      }
    };
  }

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Communities');

  // Generate filename with timestamp
  const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
  const finalFilename = filename || `Communities_Export_${timestamp}.xlsx`;

  // Write file
  XLSX.writeFile(workbook, finalFilename);
};
